import {Divider, Typography, UnstableCard as Paper} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {type ReactElement, memo, useCallback} from 'react'
import {useController, useFormContext} from 'react-hook-form'

import {TextInput, useDrawer} from '../../../../components/mobile'
import {EventGoal, useFormUserEventHandler} from '../../../../lib/metrika'
import {AddressPointSlot} from '../AddressPointSlot/AddressPointSlot'
import {SuggestDrawer} from '../SuggestDrawer/SuggestDrawer'
import type {Props} from './Address.types'
import * as classes from './Details.classes'

/**
 * Компонент с формой для заполнения деталей адреса.
 */
export const AddressDetails = memo(({addressWidgetConfig, isSource, name}: Props): ReactElement => {
  const i18n = useI18n()
  const {close, open, opened} = useDrawer()
  const {control, trigger} = useFormContext()
  const {
    fieldState: {error, invalid},
  } = useController({name: `${name}.title`, control})

  const handleReachGoal = useFormUserEventHandler()

  const handleClose = useCallback(() => {
    // при закрытии саджеста валидируем адрес
    void trigger(name).then(() => {
      close()
    })
  }, [close, name, trigger])

  const handleOpen = useCallback(() => {
    handleReachGoal(isSource ? EventGoal.AddressFromSuggestClick : EventGoal.AddressToSuggestClick)
    open()
  }, [handleReachGoal, isSource, open])

  return (
    <>
      <Paper className={classes.detailsWrapper} padding="S">
        <AddressPointSlot
          addressWidgetConfig={addressWidgetConfig}
          name={name}
          onClick={handleOpen}
          shouldShowError={false}
        />
        <div className={classes.inputsWrapper}>
          <Divider isInvalid={invalid} />
          {invalid && (
            <Typography className={classes.errorMessage} color="error" variant="caption2">
              {error?.message}
            </Typography>
          )}
          <div className={classes.inputColumns}>
            <TextInput
              label={i18n('bdui.address.input.apartment', 'Кв./офис')}
              name={`${name}.apartment`}
              size="l"
              variant="line"
            />
            <TextInput
              label={i18n('bdui.address.input.entrance', 'Подъезд')}
              name={`${name}.entrance`}
              size="l"
              variant="line"
            />
            <TextInput
              label={i18n('bdui.address.input.floor', 'Этаж')}
              name={`${name}.floor`}
              size="l"
              variant="line"
            />
            <TextInput
              label={i18n('bdui.address.input.code', 'Домофон')}
              name={`${name}.code`}
              size="l"
              variant="line"
            />
          </div>
          <TextInput
            label={i18n('bdui.address.input.comment', 'Комментарий для курьера')}
            name={`${name}.comment`}
            size="l"
            variant="line"
          />
        </div>
      </Paper>
      <SuggestDrawer
        isSource={addressWidgetConfig.route_point_index === 0}
        name={name}
        onClose={handleClose}
        opened={opened}
      />
    </>
  )
})
